'use client'

import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { filterOfferBanners, getBannerDimensions, getBaseImageUrl, getMerchantHref } from '@/constants/hooks'
import { apiOfferBanners } from '@/apis/offers'
import { OffersOffer, PaginationType } from '@/services/dataTypes'
import { ChevronDown, ChevronUp, Loader2, Sparkles } from 'lucide-react'

const C = { PRIMARY: '#F8F246', SECONDARY: '#2CBBC3', BROWN: '#8C5826', BORDER: '#ffe0c2', LIGHT: '#E1F1F2' }

interface Props {
    bannerResponse: OffersOffer[]
    domain: string
    mer_slug: string
    slug_type: string
    merchantId: string
    companyId: string
    pagination?: PaginationType
}

const EventSidebarBanner = ({ bannerResponse, domain, mer_slug, slug_type, merchantId, companyId, pagination }: Props) => {
    const [banners, setBanners] = useState<OffersOffer[]>([])
    const [page, setPage]       = useState(2)
    const [hasMore, setHasMore] = useState(true)
    const [loading, setLoading] = useState(false)
    const [expanded, setExpanded] = useState(false)

    useEffect(() => {
        setBanners(filterOfferBanners(bannerResponse, 20, 2000, 20, 2000))
        if (!pagination?.next_page) setHasMore(false)
    }, [bannerResponse])

    const loadMore = async () => {
        if (loading || !hasMore) return
        setLoading(true)
        try {
            const res = await apiOfferBanners(merchantId, companyId, page)
            const more = filterOfferBanners(res.data?.offers || [], 20, 2000, 20, 2000)
            setBanners(prev => [...prev, ...more])
            if (res?.data?.pagination?.next_page) { setPage(p => p + 1); setExpanded(true) }
            else setHasMore(false)
        } catch (e) { console.error(e) }
        finally { setLoading(false) }
    }

    const showLess = () => {
        setBanners(filterOfferBanners(bannerResponse, 20, 2000, 20, 2000))
        setPage(2); setHasMore(true); setExpanded(false)
    }

    if (!banners.length) return null

    return (
        <div className="rounded-2xl overflow-hidden" style={{ background: '#ffffff', border: `1.5px solid ${C.BORDER}` }}>
            {/* Header */}
            <div className="flex items-center gap-2 px-4 py-3"
                style={{ background: `linear-gradient(135deg, ${C.PRIMARY} 0%, #ffb347 100%)` }}>
                <Sparkles size={14} style={{ color: C.BROWN }} />
                <h4 className="text-xs font-black uppercase tracking-wider" style={{ color: C.BROWN }}>
                    Banner Offers
                </h4>
            </div>

            {/* Banner items */}
            <div className="p-3 space-y-2">
                {banners.map((offer_data, i) => {
                    const dim = getBannerDimensions(offer_data)
                    const { offer_title, banner_image, url } = offer_data?.offer ?? {}
                    const merchantHref = getMerchantHref(offer_data?.merchant, mer_slug, slug_type)
                    const imgSrc = getBaseImageUrl(domain, banner_image, '')

                    if (!imgSrc) return null
                    return (
                        <Link key={i} href={url?.startsWith('/') ? url : `/${url ?? ''}`}
                            target="_blank" rel="nofollow noopener"
                            className="block rounded-xl overflow-hidden transition-all hover:scale-[1.02] hover:shadow-md"
                            style={{ border: `1px solid ${C.BORDER}` }}>
                            <Image
                                src={imgSrc}
                                alt={offer_title || 'Banner offer'}
                                width={dim?.width || 300}
                                height={dim?.height || 100}
                                className="w-full object-cover"
                            />
                        </Link>
                    )
                })}
            </div>

            {/* Load more / less */}
            {(hasMore || expanded) && (
                <div className="flex gap-2 px-3 pb-3">
                    {hasMore && (
                        <button onClick={loadMore} disabled={loading}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold rounded-xl transition-all disabled:opacity-50"
                            style={{ background: C.LIGHT, color: C.SECONDARY }}>
                            {loading
                                ? <><Loader2 size={11} className="animate-spin" /> Loading...</>
                                : <><ChevronDown size={11} /> More</>}
                        </button>
                    )}
                    {expanded && (
                        <button onClick={showLess}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold rounded-xl transition-all"
                            style={{ background: `${C.BORDER}60`, color: C.BROWN }}>
                            <ChevronUp size={11} /> Less
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

export default EventSidebarBanner
